import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  X,
  MessageSquare,
  Heart,
  Trash2,
  Eye,
  Smile,
  Pin,
  Building2,
  BarChart2,
  ArrowLeft,
  Edit2,
  Search,
  BookOpen,
  Archive,
  Filter,
  MoreVertical,
  ChevronDown,
  Power,
  Film,
  Image as ImageIcon,
  Upload,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import ModerationQueue from "../../components/Admin/ModerationQueue";
import ReactionModal from "../../components/Admin/ReactionModal";
import { getEmoji } from "../../utils/communityReactions";

import FileUpload from "../../components/Common/FileUpload";
import useGlobalSearch from "../../hooks/useGlobalSearch";
import Select from "../../components/Common/Select";
import Button from "../../components/Common/Button";

const EMPTY_POST_DRAFT = {
  title: "",
  content: "",
  targetAudience: "all",
  targetCollege: "",
  targetDegree: "",
  type: "announcement",
  channelType: "discussion",
  media: { url: "", resourceType: "image" },
  hasPoll: false,
  poll: null,
};

const CommunityHub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { searchQuery: searchTerm, setSearchQuery: setSearchTerm, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch("", 400, () => setCurrentPage(1));
  const [collegeFilter, setCollegeFilter] = useState("all");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [pinnedFilter, setPinnedFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [postStatus, setPostStatus] = useState("active");
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [hasFetchedColleges, setHasFetchedColleges] = useState(false);
  const [hasFetchedDegrees, setHasFetchedDegrees] = useState(false);
  const [viewingPost, setViewingPost] = useState(null);

  // College banners (shown on the student dashboard of the chosen college)
  const [bannerCollege, setBannerCollege] = useState("");
  const [banners, setBanners] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [bannerDraft, setBannerDraft] = useState({ items: [], resourceType: "image", message: "" });
  const [savingBanner, setSavingBanner] = useState(false);

  // Leadership media — ONE global slideshow beside the student login page,
  // shown to every visitor before login (GET/PUT /v1/login-media).
  const [leaderDraft, setLeaderDraft] = useState({ url: "", resourceType: "image" });
  const [savingLeader, setSavingLeader] = useState(false);
  const [loginMediaItems, setLoginMediaItems] = useState([]);
  const [hasFetchedLoginMedia, setHasFetchedLoginMedia] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [newPost, setNewPost] = useState(EMPTY_POST_DRAFT);
  const [openSection, setOpenSection] = useState({ basics: true, audience: false, extras: false });
  const [showMediaUploader, setShowMediaUploader] = useState(false);

  const [activePicker, setActivePicker] = useState(null);
  const [reactionModalPost, setReactionModalPost] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [isReplying, setIsReplying] = useState(false);

  const activeFilterCount = [
    collegeFilter !== "all",
    audienceFilter !== "all",
    pinnedFilter !== "all",
    dateFilter !== "",
    postStatus === "archived",
  ].filter(Boolean).length;

  const resetPostEditor = () => {
    setShowCreateModal(false);
    setIsEditing(false);
    setEditingPostId(null);
    setNewPost(EMPTY_POST_DRAFT);
    setOpenSection({ basics: true, audience: false, extras: false });
    setShowMediaUploader(false);
  };

  const resetFilters = () => {
    setCollegeFilter("all");
    setAudienceFilter("all");
    setPinnedFilter("all");
    setDateFilter("");
    setPostStatus("active");
    setCurrentPage(1);
  };

  const getChannelLabel = (channelType) => {
    const labels = {
      discussion: "General",
      announcement: "Announcement",
      news: "News",
      event: "Event",
      update: "Update",
      support: "Support",
      mentor: "Mentor",
      coach: "Mentor",
    };

    return labels[channelType] || "General";
  };

  const normalizePost = (post) => ({
    ...post,
    reactions: Array.isArray(post?.reactions)
      ? post.reactions.map(r => ({ ...r, type: getEmoji(r.type) }))
      : [],
    replies: Array.isArray(post?.replies) ? post.replies : [],
    views: post?.views ?? post?.viewsCount ?? 0,
  });

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} • ${timePart}`;
  };

  const updatePostState = (postId, updater) => {
    setPosts((currentPosts) =>
      currentPosts.map((post) =>
        post._id === postId ? normalizePost(updater(post)) : post,
      ),
    );

    setViewingPost((currentPost) => {
      if (!currentPost || currentPost._id !== postId) return currentPost;
      return normalizePost(updater(currentPost));
    });
  };

  useEffect(() => {
    if (!hasFetchedColleges) {
      fetchColleges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetchedColleges]);

  useEffect(() => {
    if (activeTab !== "banners" || hasFetchedLoginMedia) return;
    (async () => {
      try {
        const { data } = await api.get("/v1/login-media");
        if (data.success) setLoginMediaItems(data.data || []);
      } catch (error) {
        console.error("Error fetching login media:", error);
      }
      setHasFetchedLoginMedia(true);
    })();
  }, [activeTab, hasFetchedLoginMedia]);

  useEffect(() => {
    if (user?.role === "college_admin" && !hasFetchedDegrees) {
      fetchDegrees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetchedDegrees, user?.role]);

  useEffect(() => {
    setCurrentPage(1);
  }, [collegeFilter, debouncedSearch, activeTab, postStatus, audienceFilter, pinnedFilter, dateFilter]);

  useEffect(() => {
    if (activeTab !== "overview") return;
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collegeFilter, debouncedSearch, activeTab, postStatus, audienceFilter, pinnedFilter, dateFilter, currentPage]);

  // Once title & content are filled in, surface the audience step automatically
  // so people don't have to remember to open it themselves.
  useEffect(() => {
    if (newPost.title.trim() && newPost.content.trim()) {
      setOpenSection((s) => (s.audience ? s : { ...s, audience: true }));
    }
  }, [newPost.title, newPost.content]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showCreateModal) resetPostEditor();
        if (viewingPost) setViewingPost(null);
        if (showFiltersDrawer) setShowFiltersDrawer(false);
        if (openActionMenu) setOpenActionMenu(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, viewingPost, showFiltersDrawer, openActionMenu]);

  useEffect(() => {
    if (!openActionMenu) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest("[data-action-menu]")) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openActionMenu]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 10, status: postStatus };

      if (collegeFilter !== "all") {
        params.targetCollege = collegeFilter;
      }
      if (audienceFilter !== "all") {
        params.targetAudience = audienceFilter;
      }
      if (pinnedFilter !== "all") {
        params.isPinned = pinnedFilter;
      }
      if (dateFilter) {
        params.date = dateFilter;
      }

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const { data } = await api.get("/community", { params });

      const items = data.docs || data.data || [];
      setPosts(Array.isArray(items) ? items.map(normalizePost) : []);
      if (data.totalPages) {
        setTotalPages(data.totalPages);
      } else if (data.pagination?.totalPages) {
        setTotalPages(data.pagination.totalPages);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to load community posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchColleges = async () => {
    try {
      const { data } = await api.get("/colleges?limit=1000");
      if (data.success) {
        setColleges(data.data);
      }
      setHasFetchedColleges(true);
    } catch (error) {
      console.error("Error fetching colleges:", error);
      toast.error("Failed to load colleges");
      setHasFetchedColleges(true);
    }
  };

  const fetchBanners = async (collegeId) => {
    if (!collegeId) return;
    try {
      setLoadingBanners(true);
      const { data } = await api.get(`/v1/college-banners/college/${collegeId}?all=true`);
      if (data.success) setBanners(data.data || []);
    } catch (error) {
      toast.error("Failed to load banners");
    } finally {
      setLoadingBanners(false);
    }
  };

  const ALL_COLLEGES = "__all__";

  const handleBannerCollegeChange = (collegeId) => {
    setBannerCollege(collegeId);
    setBanners([]);
    if (collegeId && collegeId !== ALL_COLLEGES) fetchBanners(collegeId);
  };

  const handleBannerCreate = async () => {
    if (!bannerCollege) {
      toast.error("Select a college first");
      return;
    }
    if (bannerDraft.items.length === 0) {
      toast.error("Upload at least one photo or video");
      return;
    }
    // One banner record per item per college — every student dashboard picks
    // them up through the existing per-college banner fetch, no special cases.
    const targets = bannerCollege === ALL_COLLEGES ? colleges.map((c) => c._id) : [bannerCollege];
    try {
      setSavingBanner(true);
      const posts = [];
      targets.forEach((collegeId) => {
        bannerDraft.items.forEach((item) => {
          posts.push(
            api.post("/v1/college-banners", {
              collegeId,
              image: item.url,
              resourceType: item.resourceType,
              message: bannerDraft.message,
            }),
          );
        });
      });
      const results = await Promise.allSettled(posts);
      const ok = results.filter((r) => r.status === "fulfilled").length;
      if (ok === 0) throw new Error("Failed to publish banners");
      const n = bannerDraft.items.length;
      toast.success(
        bannerCollege === ALL_COLLEGES
          ? `Published ${n} banner${n > 1 ? "s" : ""} to ${targets.length} colleges`
          : `Published ${ok} banner${ok > 1 ? "s" : ""} to the student dashboard`,
      );
      setShowBannerModal(false);
      setBannerDraft({ items: [], resourceType: "image", message: "" });
      if (bannerCollege !== ALL_COLLEGES) fetchBanners(bannerCollege);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to publish banners");
    } finally {
      setSavingBanner(false);
    }
  };

  const toggleBannerActive = async (banner) => {
    try {
      setBanners((prev) => prev.map((b) => (b._id === banner._id ? { ...b, isActive: !banner.isActive } : b)));
      await api.put(`/v1/college-banners/${banner._id}`, { isActive: !banner.isActive });
      toast.success(banner.isActive ? "Banner hidden from students" : "Banner is now live");
    } catch (error) {
      toast.error("Failed to update banner");
      fetchBanners(bannerCollege);
    }
  };

  const handleLeaderAdd = async () => {
    if (!leaderDraft.url) {
      toast.error(`Upload a ${leaderDraft.resourceType === "video" ? "video" : "photo"} first`);
      return;
    }
    const item = { url: leaderDraft.url, resourceType: leaderDraft.resourceType };
    const next = [...loginMediaItems, item];
    try {
      setSavingLeader(true);
      const { data } = await api.put("/v1/login-media", { items: next });
      if (data.success) {
        toast.success("Added to the login page slideshow");
        setLoginMediaItems(data.data || next);
        setLeaderDraft({ url: "", resourceType: "image" });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add slideshow item");
    } finally {
      setSavingLeader(false);
    }
  };

  const handleLeaderRemove = async (index) => {
    const next = loginMediaItems.filter((_, i) => i !== index);
    try {
      const { data } = await api.put("/v1/login-media", { items: next });
      if (data.success) {
        toast.success("Removed from the slideshow");
        setLoginMediaItems(data.data || next);
      }
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const handleBannerDelete = async (bannerId) => {
    if (!window.confirm("Delete this banner permanently?")) return;
    try {
      setBanners((prev) => prev.filter((b) => b._id !== bannerId));
      await api.delete(`/v1/college-banners/${bannerId}`);
      toast.success("Banner deleted");
    } catch (error) {
      toast.error("Failed to delete banner");
      fetchBanners(bannerCollege);
    }
  };

  const fetchDegrees = async () => {
    try {
      const { data } = await api.get("/college-degrees");
      if (data.success) {
        setDegrees(data.data);
      }
      setHasFetchedDegrees(true);
    } catch (error) {
      console.error("Error fetching degrees:", error);
      toast.error("Failed to load degrees");
      setHasFetchedDegrees(true);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.title || !newPost.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setIsCreatingPost(true);
      const payload = {
        ...newPost,
        category: getChannelLabel(newPost.channelType),
      };

      if (newPost.hasPoll && newPost.poll && newPost.poll.question) {
        payload.poll = {
          question: newPost.poll.question,
          options: newPost.poll.options
            .filter(opt => opt && opt.trim())
            .map(opt => ({ text: opt.trim(), voters: [] }))
        };
      } else {
        payload.poll = null;
      }
      delete payload.hasPoll;

      if (user?.role === "college_admin") {
        payload.targetAudience = "specific_college";
        payload.targetCollege = user?.college || user?.collegeId;
        if (!payload.targetDegree) {
          payload.targetDegree = null;
        }
      } else {
        delete payload.targetDegree;
      }

      if (isEditing) {
        const { data } = await api.put(`/community/${editingPostId}`, payload);
        if (data.success) {
          toast.success("Post updated successfully!");
          resetPostEditor();
          fetchPosts();
        }
      } else {
        const { data } = await api.post("/community", payload);
        if (data.success) {
          toast.success("Post created successfully!");
          resetPostEditor();
          fetchPosts();
        }
      }
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error(error.response?.data?.message || (isEditing ? "Failed to update post" : "Failed to create post"));
    } finally {
      setIsCreatingPost(false);
    }
  };

  const handleEditClick = (post) => {
    const hasMedia = !!post.media?.url;
    const hasPoll = !!post.poll?.question;
    setNewPost({
      title: post.title,
      content: post.content,
      targetAudience: post.targetAudience || "all",
      targetCollege: post.targetCollege?._id || post.targetCollege || "",
      targetDegree: post.targetDegree?._id || post.targetDegree || "",
      type: post.type || "announcement",
      channelType: post.channelType || "discussion",
      media: post.media || { url: "", resourceType: "image" },
      hasPoll,
      poll: post.poll ? { question: post.poll.question, options: post.poll.options.map(opt => opt.text) } : null,
    });
    setOpenSection({ basics: true, audience: true, extras: hasMedia || hasPoll });
    setShowMediaUploader(hasMedia);
    setEditingPostId(post._id);
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to remove this post?")) return;

    try {
      await api.delete(`/community/${postId}`);
      toast.success("Post removed successfully");
      setPosts(posts.filter((post) => post._id !== postId));
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    }
  };

  const handleTogglePin = async (postId, currentPinStatus) => {
    try {
      await api.patch(`/community/${postId}/pin`);
      toast.success(currentPinStatus ? "Post unpinned" : "Post pinned");
      setPosts(
        posts.map((p) =>
          p._id === postId ? { ...p, isPinned: !currentPinStatus } : p,
        ),
      );
    } catch (error) {
      console.error("Error toggling pin:", error);
      toast.error("Failed to update pin status");
    }
  };

  const handleToggleArchive = async (postId, currentStatus) => {
    try {
      await api.patch(`/community/${postId}/archive`);
      toast.success(currentStatus === "archived" ? "Post unarchived" : "Post archived");
      setPosts(posts.filter((p) => p._id !== postId));
    } catch (error) {
      console.error("Error toggling archive:", error);
      toast.error("Failed to update archive status");
    }
  };

  const handleReaction = async (postId, type = "like") => {
    try {
      const { data } = await api.put(`/community/${postId}/like`, { type });
      if (data.success) {
        updatePostState(postId, (post) => ({
          ...post,
          reactions: data.data?.reactions || [],
        }));
      }
    } catch (error) {
      console.error("Error reacting to post:", error);
      toast.error("Failed to update reaction");
    }
  };

  const handleAddReply = async (e) => {
    e.preventDefault();
    if (!viewingPost || !replyDraft.trim()) return;

    try {
      setIsReplying(true);
      const { data } = await api.post(`/community/${viewingPost._id}/comment`, {
        content: replyDraft.trim(),
      });
      if (data.success) {
        updatePostState(viewingPost._id, (post) => ({
          ...post,
          replies: data.data || post.replies,
        }));
        setReplyDraft("");
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      toast.error(error.response?.data?.message || "Failed to add reply");
    } finally {
      setIsReplying(false);
    }
  };

  const getUserReaction = (post) => {
    return post.reactions?.find(
      (r) => (r.user?._id || r.user)?.toString() === user?._id?.toString()
    )?.type;
  };

  const getAudienceBadge = (audience) => {
    const styles = {
      all: "bg-blue-100 text-blue-700",
      college_admins: "bg-[#1a3884]/10 text-[#1a3884]",
      specific_college: "bg-[#1a3884]/10 text-[#1a3884]",
      specific_college_admin: "bg-pink-100 text-pink-700",
      colleges: "bg-[#1a3884]/10 text-[#1a3884]",
      students: "bg-green-100 text-green-700",
    };
    return styles[audience] || styles.all;
  };

  const getChannelBadge = (channelType) => {
    const normalized = (channelType || "discussion").toLowerCase();

    if (normalized === "support") {
      return { label: "SUPPORT", tone: "bg-red-100 text-red-700 border-red-200" };
    }

    if (normalized === "mentor") {
      return {
        label: "MENTOR",
        tone: "bg-blue-100 text-blue-700 border-blue-200",
      };
    }
    return { label: "GENERAL", tone: "bg-gray-100 text-gray-700 border-gray-200" };
  };

  const canModerate = ["admin", "college_admin", "office_admin"].includes(user?.role);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147]">
        <div className="flex items-center gap-4 text-left">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="space-y-0.5">
            <h1 className="text-[22px] font-black text-[#1a3884] dark:text-[#4f7dff] tracking-tight">Community Hub</h1>
            <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
              <MessageSquare className="w-4 h-4 text-[#1a3884]/60 dark:text-[#4f7dff]/60" />
              Manage discussions and institutional announcements
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {["superadmin", "admin", "office_admin", "college_admin"].includes(user?.role) && (
            <div className="flex p-1 bg-gray-50 dark:bg-white/5 border border-slate-250 dark:border-white/10 rounded-xl">
              <button
                onClick={() => setActiveTab("overview")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                  activeTab === "overview"
                    ? "bg-[#1a3884] text-white shadow-md"
                    : "text-slate-500 dark:text-gray-400 hover:text-slate-900"
                }`}
              >
                Feed
              </button>
              <button
                onClick={() => setActiveTab("moderation")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                  activeTab === "moderation"
                    ? "bg-[#1a3884] text-white shadow-md"
                    : "text-slate-500 dark:text-gray-400 hover:text-slate-900"
                }`}
              >
                Reports
              </button>
              {["admin", "college_admin"].includes(user?.role) && (
                <button
                  onClick={() => setActiveTab("banners")}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                    activeTab === "banners"
                      ? "bg-[#1a3884] text-white shadow-md"
                      : "text-slate-500 dark:text-gray-400 hover:text-slate-900"
                  }`}
                >
                  Banners
                </button>
              )}
            </div>
          )}
          {["superadmin", "admin", "college_admin", "office_admin"].includes(user?.role) && (
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              icon={Send}
              className="border-none bg-[#1a3884] hover:bg-[#132A55] text-white rounded-xl px-5 py-2.5 shadow-md"
            >
              Create New Post
            </Button>
          )}
        </div>
      </div>

      {activeTab === "moderation" && (
        <div className="w-full bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/20 shadow-sm p-6">
          <ModerationQueue />
        </div>
      )}

      {activeTab === "banners" && (
        <>
          <div className="w-full bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/20 shadow-sm p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-5 mb-6 border-b border-gray-100 dark:border-[#1a3884]/20">
              <div>
                <h2 className="text-lg font-bold text-[#1a3884] dark:text-[#4f7dff]">College Banners</h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
                  Photos and videos shown at the top of the student dashboard for the selected college.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto">
                <Select
                  value={bannerCollege}
                  onChange={(e) => handleBannerCollegeChange(e.target.value)}
                  placeholder="Select a college..."
                  options={[
                    { value: ALL_COLLEGES, label: "All Colleges (every student)" },
                    ...colleges
                      .map((c) => ({ value: c._id, label: c.collegeName || c.name }))
                      .filter((o) => o.label)
                      .sort((a, b) => a.label.localeCompare(b.label)),
                  ]}
                  className="w-full sm:w-72"
                />
                <Button
                  variant="primary"
                  onClick={() => setShowBannerModal(true)}
                  icon={Upload}
                  disabled={!bannerCollege}
                  className="border-none bg-[#1a3884] hover:bg-[#132A55] text-white rounded-xl px-5 py-2.5 shadow-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Upload Banner
                </Button>
              </div>
            </div>

            {!bannerCollege ? (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">Select a college to view and manage its banners</p>
              </div>
            ) : bannerCollege === ALL_COLLEGES ? (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                <Upload className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium text-gray-600 dark:text-gray-300">
                  Publishing to all {colleges.length} colleges
                </p>
                <p className="text-sm mt-1 max-w-md mx-auto">
                  The uploaded banner will appear on every student's dashboard. To view or manage
                  the banners of one college afterwards, select that college from the list.
                </p>
              </div>
            ) : loadingBanners ? (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">Loading banners...</div>
            ) : banners.length === 0 ? (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No banners yet for this college</p>
                <p className="text-sm mt-1">Upload the first one — students will see it on their dashboard.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {banners.map((banner) => (
                  <div
                    key={banner._id}
                    className="rounded-xl border border-gray-100 dark:border-[#1a3884]/20 overflow-hidden bg-gray-50 dark:bg-white/5"
                  >
                    <div className="relative aspect-[3/1] bg-slate-200 dark:bg-[#002A5C]">
                      {banner.resourceType === "video" ? (
                        <video src={banner.image} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                      ) : (
                        <img src={banner.image} alt="College banner" className="w-full h-full object-cover" />
                      )}
                      <span
                        className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                          banner.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {banner.isActive ? "Live" : "Hidden"}
                      </span>
                      <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-black/50 text-white flex items-center gap-1">
                        {banner.resourceType === "video" ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                        {banner.resourceType === "video" ? "Video" : "Image"}
                      </span>
                    </div>
                    <div className="p-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                          {banner.message || <span className="italic text-gray-400">No message</span>}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Added {new Date(banner.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => toggleBannerActive(banner)}
                          title={banner.isActive ? "Hide from students" : "Show to students"}
                          className={`p-2 rounded-lg transition-colors ${
                            banner.isActive
                              ? "bg-green-50 text-green-600 hover:bg-green-100"
                              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          }`}
                        >
                          <Power className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleBannerDelete(banner._id)}
                          title="Delete banner"
                          className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(
            <div className="w-full bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/20 shadow-sm p-6">
              <div className="pb-5 mb-6 border-b border-gray-100 dark:border-[#1a3884]/20">
                <h2 className="text-lg font-bold text-[#1a3884] dark:text-[#4f7dff]">Leadership Media — Login Page Slideshow</h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
                  One slideshow of photos and videos playing beside the student login page —{" "}
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    shown to every student, no college selection needed
                  </span>. Items play in the order added.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Current slideshow</p>
                  {(() => {
                    const items = loginMediaItems;
                    if (items.length === 0) {
                      return (
                        <div className="aspect-video rounded-xl border border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center text-gray-400 text-sm">
                          No items yet — students see the default video
                        </div>
                      );
                    }
                    return (
                      <div className="grid grid-cols-2 gap-3">
                        {items.map((item, idx) => (
                          <div
                            key={`${item.url}-${idx}`}
                            className="relative rounded-xl overflow-hidden border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5"
                          >
                            {item.resourceType === "video" ? (
                              <video src={item.url} className="aspect-video w-full object-cover" muted playsInline />
                            ) : (
                              <img src={item.url} alt={`Slideshow item ${idx + 1}`} className="aspect-video w-full object-cover" />
                            )}
                            <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase bg-black/50 text-white flex items-center gap-1">
                              {item.resourceType === "video" ? <Film className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}
                              {idx + 1}
                            </span>
                            <button
                              onClick={() => handleLeaderRemove(idx)}
                              className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors"
                              title="Remove from slideshow"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Add to slideshow</p>
                  <div className="flex gap-2 mb-3">
                    {["video", "image"].map((t) => (
                      <button
                        key={t}
                        onClick={() => setLeaderDraft({ url: "", resourceType: t })}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all border ${
                          leaderDraft.resourceType === t
                            ? "bg-[#1a3884] text-white border-[#1a3884]"
                            : "bg-white dark:bg-white/5 text-gray-500 border-gray-200 dark:border-white/10 hover:border-[#1a3884]/50"
                        }`}
                      >
                        {t === "video" ? <Film className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
                        {t === "video" ? "Video" : "Photo"}
                      </button>
                    ))}
                  </div>
                  {leaderDraft.url ? (
                    <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-white/10">
                      {leaderDraft.resourceType === "video" ? (
                        <video src={leaderDraft.url} className="aspect-video w-full object-cover" muted loop autoPlay playsInline />
                      ) : (
                        <img src={leaderDraft.url} alt="New leadership message preview" className="aspect-video w-full object-cover" />
                      )}
                      <button
                        onClick={() => setLeaderDraft((prev) => ({ ...prev, url: "" }))}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <FileUpload
                      type={leaderDraft.resourceType}
                      onUploadComplete={(data) =>
                        setLeaderDraft((prev) => ({ ...prev, url: data.url || data.secure_url || data }))
                      }
                      label={`Upload ${leaderDraft.resourceType === "video" ? "video" : "photo"}`}
                    />
                  )}
                  <div className="flex justify-end mt-3">
                    <Button
                      variant="primary"
                      onClick={handleLeaderAdd}
                      loading={savingLeader}
                      disabled={!leaderDraft.url || savingLeader}
                      className="border-none bg-[#1a3884] hover:bg-[#132A55] text-white rounded-xl px-5 py-2.5 shadow-md disabled:opacity-50"
                    >
                      Add to Slideshow
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showBannerModal && createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
              onClick={() => !savingBanner && setShowBannerModal(false)}
            >
              <div
                className="w-full max-w-lg bg-white dark:bg-[#002147] rounded-2xl shadow-xl border border-gray-100 dark:border-[#1a3884]/20 p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-bold text-[#1a3884] dark:text-[#4f7dff]">Upload Banner</h3>
                  <button
                    onClick={() => setShowBannerModal(false)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Banner type
                    </label>
                    <div className="flex gap-2">
                      {["image", "video"].map((t) => (
                        <button
                          key={t}
                          onClick={() => setBannerDraft((prev) => ({ ...prev, resourceType: t }))}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all border ${
                            bannerDraft.resourceType === t
                              ? "bg-[#1a3884] text-white border-[#1a3884]"
                              : "bg-white dark:bg-white/5 text-gray-500 border-gray-200 dark:border-white/10 hover:border-[#1a3884]/50"
                          }`}
                        >
                          {t === "video" ? <Film className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
                          {t === "video" ? "Video" : "Photo"}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    {bannerDraft.items.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {bannerDraft.items.map((item, idx) => (
                          <div
                            key={`${item.url}-${idx}`}
                            className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-white/10"
                          >
                            {item.resourceType === "video" ? (
                              <video src={item.url} className="w-full aspect-[3/1] object-cover" muted playsInline />
                            ) : (
                              <img src={item.url} alt={`Banner ${idx + 1}`} className="w-full aspect-[3/1] object-cover" />
                            )}
                            <button
                              onClick={() =>
                                setBannerDraft((prev) => ({
                                  ...prev,
                                  items: prev.items.filter((_, i) => i !== idx),
                                }))
                              }
                              className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors"
                              title="Remove"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <FileUpload
                      key={`${bannerDraft.resourceType}-${bannerDraft.items.length}`}
                      type={bannerDraft.resourceType}
                      onUploadComplete={(data) =>
                        setBannerDraft((prev) => ({
                          ...prev,
                          items: [
                            ...prev.items,
                            { url: data.url || data.secure_url || data, resourceType: prev.resourceType },
                          ],
                        }))
                      }
                      label={`Upload ${bannerDraft.resourceType === "video" ? "video" : "photo"} — you can add several`}
                    />
                    <p className="text-xs text-gray-400 mt-1.5">
                      Wide images work best (roughly 3:1, e.g. 1440×480). Each upload joins the
                      list above; publish sends them all as a slideshow.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Message shown on the banner (optional)
                    </label>
                    <textarea
                      value={bannerDraft.message}
                      onChange={(e) => setBannerDraft({ ...bannerDraft, message: e.target.value })}
                      rows={2}
                      placeholder="e.g. Placement drive registrations close this Friday!"
                      className="w-full rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1a3884]/40"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      onClick={() => setShowBannerModal(false)}
                      disabled={savingBanner}
                      className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    >
                      Cancel
                    </button>
                    <Button
                      variant="primary"
                      onClick={handleBannerCreate}
                      loading={savingBanner}
                      disabled={bannerDraft.items.length === 0 || savingBanner}
                      className="border-none bg-[#1a3884] hover:bg-[#132A55] text-white rounded-xl px-5 py-2.5 shadow-md disabled:opacity-50"
                    >
                      {bannerDraft.items.length > 1
                        ? `Publish ${bannerDraft.items.length} Banners`
                        : "Publish Banner"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
        </>
      )}

      {activeTab === "overview" && (
        <>
          {/* Search + Filters toolbar — collapsed by default */}
          <div className="w-full mb-6 bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/20 shadow-sm p-6">
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
              <div className="relative flex-1 w-full">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search posts by title or content..."
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 dark:bg-[#001f40] focus:bg-white dark:focus:bg-[#001f40] text-gray-900 dark:text-white transition-all outline-none h-[42px]"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFiltersDrawer(true)}
                className="relative flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors h-[42px] shrink-0"
              >
                <Filter size={16} />
                Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-[#1a3884] text-white text-[10px] font-bold rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="w-full flex flex-col gap-6">
            <div className="space-y-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a3884] mx-auto"></div>
                  <p className="mt-4 text-gray-500">
                    Loading community updates...
                  </p>
                </div>
              ) : posts.length > 0 ? (
                posts.map((post) => {
                  const channelBadge = getChannelBadge(post.channelType);
                  const authorId = (post.author?._id || post.author)?.toString();
                  const isAuthor = authorId === user?._id?.toString();
                  const canEditDelete = isAuthor || user?.role === "admin";
                  const showActionMenu = canEditDelete || canModerate;

                  return (
                    <div
                      key={post._id}
                      className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/20 overflow-hidden hover:shadow-md transition-all duration-200 p-6 md:p-8"
                    >
                      <div className="flex items-center justify-between gap-4 mb-5">
                        <div className="flex items-center gap-3.5">
                          <div className="w-10 h-10 bg-[#1a3884]/10 dark:bg-[#4f7dff]/10 rounded-full flex items-center justify-center text-[#1a3884] dark:text-[#4f7dff] font-black border border-[#1a3884]/20 dark:border-[#4f7dff]/20 shrink-0 text-sm">
                            {post.author?.fullName?.[0] || "A"}
                          </div>
                          <div className="flex flex-col gap-1">
                            <h3 className="font-bold text-gray-900 dark:text-white text-sm leading-tight">
                              {post.author?.fullName || "Admin"}
                            </h3>
                            <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 flex-wrap">
                              <span>{formatDateTime(post.createdAt)}</span>
                              {post.isEdited && post.editedAt && (
                                <>
                                  <span className="opacity-40">•</span>
                                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                    Edited {formatDateTime(post.editedAt)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`px-2.5 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${channelBadge.tone}`}>
                            {channelBadge.label}
                          </span>
                          <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${getAudienceBadge(post.targetAudience)}`}>
                            {post.targetAudience === "specific_college"
                              ? "Specific College"
                              : post.targetAudience === "specific_college_admin"
                                ? "Specific College Admin"
                                : post.targetAudience === "college_admins"
                                  ? "College Admins"
                                  : post.targetAudience === "all"
                                    ? "All"
                                    : post.targetAudience.charAt(0).toUpperCase() + post.targetAudience.slice(1)}
                          </span>

                          {showActionMenu && (
                            <div className="relative" data-action-menu>
                              <button
                                onClick={() => setOpenActionMenu(openActionMenu === post._id ? null : post._id)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 transition-colors"
                                title="Post actions"
                              >
                                <MoreVertical size={16} />
                              </button>
                              <AnimatePresence>
                                {openActionMenu === post._id && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 6, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 6, scale: 0.97 }}
                                    transition={{ duration: 0.12 }}
                                    className="absolute right-0 mt-2 w-44 bg-white dark:bg-[#002147] rounded-xl shadow-xl border border-gray-100 dark:border-[#1a3884]/20 py-1.5 z-30"
                                  >
                                    {canEditDelete && (
                                      <button
                                        onClick={() => { setOpenActionMenu(null); handleEditClick(post); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                      >
                                        <Edit2 size={14} /> Edit
                                      </button>
                                    )}
                                    {canModerate && (
                                      <button
                                        onClick={() => { setOpenActionMenu(null); handleTogglePin(post._id, post.isPinned); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                      >
                                        <Pin size={14} className={post.isPinned ? "fill-current" : ""} />
                                        {post.isPinned ? "Unpin" : "Pin"}
                                      </button>
                                    )}
                                    {canModerate && (
                                      <button
                                        onClick={() => { setOpenActionMenu(null); handleToggleArchive(post._id, post.status); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                      >
                                        <Archive size={14} /> {post.status === "archived" ? "Unarchive" : "Archive"}
                                      </button>
                                    )}
                                    {canEditDelete && (
                                      <button
                                        onClick={() => { setOpenActionMenu(null); handleDeletePost(post._id); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                      >
                                        <Trash2 size={14} /> Delete
                                      </button>
                                    )}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="md:pl-[54px]">
                        <div
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={async () => {
                            setViewingPost(post);
                            try {
                              const { data: viewData } = await api.patch(`/community/${post._id}/view`);
                              if (viewData.success) {
                                updatePostState(post._id, (currentPost) => ({
                                  ...currentPost,
                                  views: viewData.data.views,
                                }));
                              }
                            } catch (e) {}
                          }}
                        >
                          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {post.title}
                          </h2>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 whitespace-pre-wrap leading-relaxed">
                            {post.content}
                          </div>
                        </div>

                        {post.poll && post.poll.question && (
                          <div className="mb-4 bg-gray-50 border border-gray-100 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <BarChart2 size={16} className="text-[#1a3884]" />
                              <h4 className="font-semibold text-gray-900">
                                {post.poll.question}
                              </h4>
                            </div>
                            <div className="space-y-2">
                              {post.poll.options?.map((option, idx) => {
                                const totalVotes = post.poll.options.reduce(
                                  (sum, opt) => sum + (opt.voters?.length || 0),
                                  0,
                                );
                                const optionVotes = option.voters?.length || 0;
                                const percentage =
                                  totalVotes === 0
                                    ? 0
                                    : Math.round((optionVotes / totalVotes) * 100);
                                return (
                                  <div
                                    key={idx}
                                    className="relative overflow-hidden rounded-md border border-gray-200 bg-white p-2 flex justify-between items-center"
                                  >
                                    <div
                                      className="absolute top-0 left-0 bottom-0 bg-[#1a3884]/10"
                                      style={{ width: `${percentage}%` }}
                                    ></div>
                                    <span className="relative z-10 text-sm break-words flex-1 pr-4">
                                      {option.text}
                                    </span>
                                    <span className="relative z-10 text-xs font-semibold text-gray-600">
                                      {percentage}% ({optionVotes})
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-3 text-xs text-gray-550 text-right">
                              Total votes:{" "}
                              {post.poll.options?.reduce(
                                (sum, opt) => sum + (opt.voters?.length || 0),
                                0,
                              )}
                            </div>
                          </div>
                        )}

                        {post.media && post.media.url && (
                          <div className="mb-4 rounded-lg overflow-hidden border border-gray-100">
                            {post.media.resourceType === "video" ? (
                              <video
                                src={post.media.url}
                                controls
                                className="w-full h-auto max-h-[300px] object-cover rounded-lg"
                              />
                            ) : (
                              <img
                                src={post.media.url}
                                alt="Post content"
                                className="w-full h-auto max-h-[300px] object-cover rounded-lg"
                              />
                            )}
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-gray-500 mb-4 pb-4 border-b border-gray-100 dark:border-[#1a3884]/20">
                          <div className="flex items-center gap-1.5">
                            <Eye size={14} className="text-gray-400" />
                            <span>{post.views || 0} Views</span>
                          </div>
                          {post.targetCollege && (
                            <div className="flex items-center gap-1.5">
                              <Building2 size={14} className="text-gray-400" />
                              <span>
                                {post.targetCollege.collegeName || `College ID: ${post.targetCollege}`}
                              </span>
                            </div>
                          )}
                          {post.targetDegree && (
                            <div className="flex items-center gap-1.5">
                              <BookOpen size={14} className="text-gray-400" />
                              <span>
                                {post.targetDegree.fullName || `Degree ID: ${post.targetDegree}`}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Reactions row */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 font-medium">
                          <div className="flex items-center gap-2">
                            {(post.reactions?.length > 0) && (
                              <button
                                onClick={() => setReactionModalPost(post)}
                                className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group border border-transparent hover:border-gray-100"
                              >
                                <div className="flex items-center -space-x-1.5">
                                  {Array.from(new Set(post.reactions.map(r => r.type)))
                                    .filter(type => ["👍", "❤️", "🔥", "💡", "🙌", "👏", "😄"].includes(type))
                                    .slice(0, 3)
                                    .map((emoji, idx) => (
                                      <div
                                        key={emoji}
                                        className="w-5 h-5 rounded-full bg-gray-50 flex items-center justify-center text-[11px] shadow-[0_0_0_2px_white] relative"
                                        style={{ zIndex: 10 - idx }}
                                      >
                                        {emoji}
                                      </div>
                                    ))}
                                </div>
                                <span className="text-xs font-semibold text-gray-550 group-hover:text-[#1a3884] transition-colors">
                                  {post.reactions.length}
                                </span>
                              </button>
                            )}

                            <div className="relative">
                              {(() => {
                                const userReaction = getUserReaction(post);
                                return (
                                  <button
                                    onClick={() => setActivePicker(activePicker === post._id ? null : post._id)}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                      userReaction
                                        ? "bg-blue-50 border border-blue-100 hover:bg-blue-100"
                                        : "bg-gray-100 hover:bg-gray-200"
                                    }`}
                                    title="React"
                                  >
                                    {userReaction ? (
                                      <span className="text-[16px] leading-none flex items-center justify-center">{userReaction}</span>
                                    ) : (
                                      <Heart size={16} className="text-gray-600 dark:text-gray-300 hover:text-red-500 transition-colors" />
                                    )}
                                  </button>
                                );
                              })()}

                              <AnimatePresence>
                                {activePicker === post._id && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute bottom-full mb-2 left-0 z-50 bg-white rounded-full shadow-xl border border-gray-100 p-1.5 flex items-center gap-1"
                                  >
                                    {["👍", "❤️", "😄", "🔥", "💡", "🙌"].map((emoji) => (
                                      <button
                                        key={emoji}
                                        onClick={() => {
                                          handleReaction(post._id, emoji);
                                          setActivePicker(null);
                                        }}
                                        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-transform hover:scale-125 hover:-translate-y-1 text-lg"
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>

                            {post.replies?.length > 0 && (
                              <button
                                onClick={async () => {
                                  setViewingPost(post);
                                }}
                                className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-[#1a3884] hover:bg-gray-50 rounded-lg transition-colors"
                              >
                                <MessageSquare size={14} />
                                {post.replies.length} {post.replies.length === 1 ? "reply" : "replies"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-16 px-6 bg-white rounded-xl border border-gray-200 border-dashed shadow-sm flex flex-col items-center justify-center">
                  {debouncedSearch ? (
                    <Search size={48} className="text-gray-300 mb-4" />
                  ) : (
                    <MessageSquare size={48} className="text-gray-300 mb-4" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {debouncedSearch
                      ? "No posts match your search"
                      : collegeFilter !== "all"
                        ? "No posts for this college"
                        : "No discussions yet"}
                  </h3>
                  <p className="text-gray-500 max-w-sm">
                    {debouncedSearch
                      ? `No results for "${debouncedSearch}". Try different keywords.`
                      : collegeFilter !== "all"
                        ? "This college has no posts yet. Posts targeted to this college will appear here."
                        : "When community members start posting updates, they will appear here."}
                  </p>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-6">
                <div className="text-xs font-semibold text-gray-505">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Filters drawer */}
          {showFiltersDrawer && createPortal(
            <div className="fixed inset-0 z-[95] flex justify-end" onClick={() => setShowFiltersDrawer(false)}>
              <div className="absolute inset-0 bg-black/30" />
              <motion.div
                initial={{ x: 360 }}
                animate={{ x: 0 }}
                transition={{ duration: 0.2 }}
                className="relative w-full max-w-sm bg-white dark:bg-[#002147] h-full shadow-2xl p-6 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Filters</h3>
                  <button
                    onClick={() => setShowFiltersDrawer(false)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-5">
                  {["superadmin", "admin", "office_admin"].includes(user?.role) && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">College</label>
                      <Select
                        value={collegeFilter}
                        onChange={(e) => setCollegeFilter(e.target.value)}
                        options={[
                          { value: "all", label: "All Colleges" },
                          { value: "smaart_admin", label: "SMAART Admin" },
                          ...colleges.map(c => ({ value: c._id, label: c.collegeName }))
                        ]}
                        className="w-full"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Audience</label>
                    <Select
                      value={audienceFilter}
                      onChange={(e) => setAudienceFilter(e.target.value)}
                      options={[
                        { value: "all", label: "Everyone" },
                        { value: "college_admins", label: "College Admins Only" },
                        { value: "specific_college", label: "Specific College" },
                        { value: "specific_college_admin", label: "Specific College Admin" }
                      ]}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Pinned status</label>
                    <Select
                      value={pinnedFilter}
                      onChange={(e) => setPinnedFilter(e.target.value)}
                      options={[
                        { value: "all", label: "All Pins" },
                        { value: "true", label: "Pinned Only" },
                        { value: "false", label: "Unpinned Only" }
                      ]}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Date</label>
                    <input
                      type="date"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white outline-none h-[42px]"
                    />
                  </div>

                  {["superadmin", "admin", "college_admin", "office_admin"].includes(user?.role) && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                      <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#001f40] p-1 rounded-lg border border-gray-200 dark:border-[#1a3884]/30">
                        <button
                          onClick={() => setPostStatus("active")}
                          className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            postStatus === "active"
                              ? "bg-white dark:bg-[#1a3884] text-gray-900 dark:text-white shadow-sm"
                              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          }`}
                        >
                          Active
                        </button>
                        <button
                          onClick={() => setPostStatus("archived")}
                          className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            postStatus === "archived"
                              ? "bg-white dark:bg-[#1a3884] text-gray-900 dark:text-white shadow-sm"
                              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          }`}
                        >
                          Archived
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 mt-8">
                  {activeFilterCount > 0 && (
                    <button
                      onClick={resetFilters}
                      className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl transition-colors"
                    >
                      Reset filters
                    </button>
                  )}
                  <button
                    onClick={() => setShowFiltersDrawer(false)}
                    className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-[#1a3884] hover:bg-[#132A55] rounded-xl transition-colors"
                  >
                    Done
                  </button>
                </div>
              </motion.div>
            </div>,
            document.body
          )}

          {/* Create / Edit Post modal — 3 collapsible steps */}
          {showCreateModal && createPortal(
            <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl animate-in fade-in zoom-in duration-200">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    {isEditing ? "Edit Post" : "Create New Post"}
                  </h2>
                  <button
                    onClick={resetPostEditor}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleCreatePost} className="flex flex-col max-h-[85vh]">
                  <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
                    {/* Step 1 — Basics */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenSection((s) => ({ ...s, basics: !s.basics }))}
                        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 text-left"
                      >
                        <span className="text-sm font-bold text-gray-800">Step 1 · What's this post?</span>
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSection.basics ? "rotate-180" : ""}`} />
                      </button>
                      {openSection.basics && (
                        <div className="p-5 space-y-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                              Post Title
                            </label>
                            <input
                              type="text"
                              value={newPost.title}
                              onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1a3884] focus:border-[#1a3884] shadow-sm transition-all text-sm"
                              placeholder="What's this update about?"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                              Post Content
                            </label>
                            <textarea
                              value={newPost.content}
                              onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                              rows={5}
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1a3884] focus:border-[#1a3884] resize-none text-sm transition-all"
                              placeholder="Write your announcement or discussion topic..."
                              required
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Step 2 — Audience */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenSection((s) => ({ ...s, audience: !s.audience }))}
                        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 text-left"
                      >
                        <span className="text-sm font-bold text-gray-800">Step 2 · Who should see it?</span>
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSection.audience ? "rotate-180" : ""}`} />
                      </button>
                      {openSection.audience && (
                        <div className="p-5 space-y-4 animate-in fade-in duration-200">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                              Target Audience
                            </label>
                            {user?.role === "college_admin" ? (
                              <div className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 text-sm flex items-center justify-between font-medium">
                                <span>My College Only</span>
                                <Pin size={14} className="text-gray-400" />
                              </div>
                            ) : (
                              <select
                                value={newPost.targetAudience}
                                onChange={(e) => setNewPost({ ...newPost, targetAudience: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1a3884] focus:border-[#1a3884] shadow-sm transition-all text-sm appearance-none bg-white"
                              >
                                <option value="all">Everyone</option>
                                <option value="college_admins">College Admins Only</option>
                                <option value="specific_college">Specific College</option>
                                <option value="specific_college_admin">Specific College Admin</option>
                              </select>
                            )}
                          </div>

                          {user?.role === "college_admin" && (
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                                Department
                              </label>
                              <select
                                value={newPost.targetDegree}
                                onChange={(e) =>
                                  setNewPost({
                                    ...newPost,
                                    targetDegree: e.target.value,
                                    targetAudience: e.target.value ? "specific_degree" : newPost.targetAudience
                                  })
                                }
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1a3884] focus:border-[#1a3884] shadow-sm text-sm appearance-none bg-white"
                              >
                                <option value="">All Departments</option>
                                {degrees.map((deg) => (
                                  <option key={deg._id} value={deg._id}>
                                    {deg.fullName} {deg.specialization && deg.specialization !== "General" ? `(${deg.specialization})` : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {["specific_college", "specific_college_admin"].includes(newPost.targetAudience) &&
                            user?.role !== "college_admin" && (
                              <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                                  Select College
                                </label>
                                <select
                                  value={newPost.targetCollege}
                                  onChange={(e) => setNewPost({ ...newPost, targetCollege: e.target.value })}
                                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1a3884] focus:border-[#1a3884] shadow-sm text-sm"
                                  required
                                >
                                  <option value="">-- Choose a College --</option>
                                  {colleges.map((college) => (
                                    <option key={college._id} value={college._id}>
                                      {college.collegeName}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                        </div>
                      )}
                    </div>

                    {/* Step 3 — Extras (optional) */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenSection((s) => ({ ...s, extras: !s.extras }))}
                        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 text-left"
                      >
                        <span className="text-sm font-bold text-gray-800">Step 3 · Add extras (optional)</span>
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSection.extras ? "rotate-180" : ""}`} />
                      </button>
                      {openSection.extras && (
                        <div className="p-5 space-y-4 animate-in fade-in duration-200">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setShowMediaUploader((v) => !v)}
                              className={`px-3.5 py-2 rounded-lg text-xs font-bold border transition-all ${
                                showMediaUploader
                                  ? "bg-[#1a3884] border-[#1a3884] text-white"
                                  : "bg-white border-gray-200 text-gray-600 hover:border-[#1a3884]/30 hover:text-[#1a3884]"
                              }`}
                            >
                              {showMediaUploader ? "− Remove image/video" : "+ Add image or video"}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setNewPost((p) => ({
                                  ...p,
                                  hasPoll: !p.hasPoll,
                                  poll: !p.hasPoll ? { question: "", options: ["", ""] } : null,
                                }))
                              }
                              className={`px-3.5 py-2 rounded-lg text-xs font-bold border transition-all ${
                                newPost.hasPoll
                                  ? "bg-[#1a3884] border-[#1a3884] text-white"
                                  : "bg-white border-gray-200 text-gray-600 hover:border-[#1a3884]/30 hover:text-[#1a3884]"
                              }`}
                            >
                              {newPost.hasPoll ? "− Remove poll" : "+ Add a poll"}
                            </button>
                          </div>

                          {showMediaUploader && (
                            <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100 space-y-4 animate-in fade-in duration-200">
                              <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                                  Media Attachment
                                </label>
                                <div className="flex items-center gap-3">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Type:</span>
                                  <div className="flex bg-white p-1 rounded-lg border border-gray-200">
                                    {['image', 'video'].map((t) => (
                                      <button
                                        key={t}
                                        type="button"
                                        onClick={() => setNewPost({
                                          ...newPost,
                                          media: { ...newPost.media, resourceType: t, url: "" }
                                        })}
                                        className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase transition-all ${newPost.media.resourceType === t
                                          ? "bg-[#1a3884] text-white shadow-sm"
                                          : "text-gray-500 hover:text-gray-700"
                                          }`}
                                      >
                                        {t}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-white rounded-xl p-2 border border-gray-200 shadow-sm">
                                  <FileUpload
                                    type={newPost.media.resourceType}
                                    onUploadComplete={(data) =>
                                      setNewPost({
                                        ...newPost,
                                        media: { ...newPost.media, url: data.url },
                                      })
                                    }
                                    label=""
                                  />
                                </div>
                                <div className="flex flex-col justify-center gap-2">
                                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                    Or External Link
                                  </label>
                                  <input
                                    type="text"
                                    value={newPost.media.url}
                                    onChange={(e) =>
                                      setNewPost({
                                        ...newPost,
                                        media: { ...newPost.media, url: e.target.value },
                                      })
                                    }
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1a3884] focus:border-[#1a3884] transition-all text-sm bg-white"
                                    placeholder={newPost.media.resourceType === 'image' ? "https://link-to-image.jpg" : "https://link-to-video.mp4"}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {newPost.hasPoll && (
                            <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100 space-y-3 animate-in fade-in duration-200">
                              <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                                  Poll Question
                                </label>
                                <input
                                  type="text"
                                  value={newPost.poll?.question || ""}
                                  onChange={(e) =>
                                    setNewPost({
                                      ...newPost,
                                      poll: { ...newPost.poll, question: e.target.value }
                                    })
                                  }
                                  className="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#1a3884]"
                                  placeholder="Ask a question..."
                                  required
                                />
                              </div>

                              <div className="space-y-2">
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                  Options
                                </label>
                                {newPost.poll?.options.map((option, index) => (
                                  <div key={index} className="flex gap-2 items-center">
                                    <input
                                      type="text"
                                      value={option}
                                      onChange={(e) => {
                                        const opts = [...newPost.poll.options];
                                        opts[index] = e.target.value;
                                        setNewPost({
                                          ...newPost,
                                          poll: { ...newPost.poll, options: opts }
                                        });
                                      }}
                                      className="flex-1 px-4 py-2 rounded-lg border border-gray-250 text-sm focus:ring-2 focus:ring-[#1a3884]"
                                      placeholder={`Option ${index + 1}`}
                                      required
                                    />
                                    {newPost.poll.options.length > 2 && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const opts = newPost.poll.options.filter((_, i) => i !== index);
                                          setNewPost({
                                            ...newPost,
                                            poll: { ...newPost.poll, options: opts }
                                          });
                                        }}
                                        className="p-2 text-red-500 hover:bg-red-55 rounded-lg"
                                      >
                                        <X size={16} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                {newPost.poll?.options.length < 5 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setNewPost({
                                        ...newPost,
                                        poll: { ...newPost.poll, options: [...newPost.poll.options, ""] }
                                      })
                                    }
                                    className="text-xs font-bold text-[#1a3884] hover:underline"
                                  >
                                    + Add Option
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl flex flex-col sm:flex-row gap-3 items-center justify-between">
                    <button
                      type="button"
                      onClick={resetPostEditor}
                      className="w-full sm:w-auto px-6 py-2.5 text-sm font-bold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all text-center"
                    >
                      Back to Hub
                    </button>
                    <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                      <button
                        type="submit"
                        disabled={isCreatingPost}
                        className="px-8 py-2.5 bg-[#1a3884] text-white rounded-xl hover:bg-[#132A55] shadow-lg shadow-[#1a3884]/20 transition-all flex items-center gap-2 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isCreatingPost ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                          <Send size={18} />
                        )}
                        <span>{isEditing ? "Save Changes" : "Confirm & Post"}</span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}
        </>
      )}

      {/* View Post Detail Modal */}
      {viewingPost && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
          onClick={() => setViewingPost(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Post Details</h2>
              <button
                onClick={() => setViewingPost(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1a3884]/10 flex items-center justify-center text-sm font-bold text-[#1a3884]">
                  {viewingPost.author?.fullName?.[0] || "A"}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {viewingPost.author?.fullName || "Unknown Author"}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-xs text-gray-500">
                      {formatDateTime(viewingPost.createdAt)}
                    </p>
                    {viewingPost.isEdited && viewingPost.editedAt && (
                      <span className="text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100 flex items-center gap-1">
                        <Edit2 className="w-3 h-3 text-gray-400" /> Edited {formatDateTime(viewingPost.editedAt)}
                      </span>
                    )}
                  </div>
                </div>
                {viewingPost.isPinned && (
                  <span className="flex items-center gap-1 ml-auto px-2 py-0.5 bg-[#1a3884]/10 text-[#1a3884] text-xs font-semibold rounded-full border border-[#1a3884]/20">
                    <Pin className="w-3 h-3 rotate-45 text-[#1a3884]" /> Pinned
                  </span>
                )}
              </div>

              <div>
                <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600">
                  {viewingPost.category || viewingPost.channelType || "general"}
                </span>
              </div>

              <h3 className="text-xl font-bold text-gray-900">
                {viewingPost.title}
              </h3>
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {viewingPost.content}
              </div>

              {viewingPost.media?.url && (
                <div className="rounded-lg overflow-hidden border border-gray-100">
                  {viewingPost.media.resourceType === "video" ? (
                    <video
                      src={viewingPost.media.url}
                      controls
                      className="w-full max-h-[400px] object-contain bg-black"
                    />
                  ) : (
                    <img
                      src={viewingPost.media.url}
                      alt="Post media"
                      className="w-full max-h-[400px] object-contain bg-gray-50"
                    />
                  )}
                </div>
              )}

              {viewingPost.poll?.question && (
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {viewingPost.poll.question}
                  </h4>
                  <div className="space-y-2">
                    {viewingPost.poll.options?.map((option, idx) => {
                      const totalVotes = viewingPost.poll.options.reduce(
                        (sum, opt) => sum + (opt.voters?.length || 0),
                        0,
                      );
                      const optionVotes = option.voters?.length || 0;
                      const percentage =
                        totalVotes === 0
                          ? 0
                          : Math.round((optionVotes / totalVotes) * 100);
                      return (
                        <div
                          key={idx}
                          className="relative overflow-hidden rounded-md border border-gray-200 bg-white p-2 flex justify-between items-center"
                        >
                          <div
                            className="absolute top-0 left-0 bottom-0 bg-[#1a3884]/10"
                            style={{ width: `${percentage}%` }}
                          ></div>
                          <span className="relative z-10 text-sm">
                            {option.text}
                          </span>
                          <span className="relative z-10 text-xs font-semibold text-gray-600">
                            {percentage}% ({optionVotes})
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 pt-3 border-t border-gray-100">
                <span className="flex items-center gap-1">
                  <Eye size={14} /> {viewingPost.views || 0} views
                </span>
                <span className="flex items-center gap-1">
                  <Heart size={14} /> {viewingPost.reactions?.filter(r => r.type === 'like').length || 0} likes
                </span>
                <span className="flex items-center gap-1">
                  <MessageSquare size={14} /> {viewingPost.replies?.length || 0} replies
                </span>
                <span className="flex items-center gap-1">
                  <Smile size={14} /> {viewingPost.reactions?.length || 0} reactions
                </span>
              </div>

              {/* Replies + reply composer */}
              <div className="pt-3 border-t border-gray-100 space-y-3">
                {viewingPost.replies?.length > 0 && (
                  <>
                    <h4 className="font-semibold text-gray-900 text-sm">
                      Replies ({viewingPost.replies.length})
                    </h4>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {viewingPost.replies.map((reply, idx) => (
                        <div key={idx} className="flex gap-3">
                          <div className="w-7 h-7 rounded-full bg-[#1a3884]/10 flex items-center justify-center text-xs font-bold text-[#1a3884] flex-shrink-0">
                            {reply.author?.fullName?.[0] || reply.user?.fullName?.[0] || "U"}
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 flex-1">
                            <div className="font-semibold text-gray-900 text-xs mb-1">
                              {reply.author?.fullName || reply.user?.fullName || "User"} •{" "}
                              <span className="text-gray-400 font-normal">
                                {new Date(reply.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-gray-700">{reply.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <form onSubmit={handleAddReply} className="flex items-center gap-2 pt-1">
                  <input
                    type="text"
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    placeholder="Add a reply..."
                    className="flex-1 px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1a3884] focus:border-[#1a3884] text-sm transition-all"
                  />
                  <button
                    type="submit"
                    disabled={!replyDraft.trim() || isReplying}
                    className="p-2.5 bg-[#1a3884] text-white rounded-xl hover:bg-[#132A55] transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    title="Send reply"
                  >
                    {isReplying ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ReactionModal
        isOpen={!!reactionModalPost}
        onClose={() => setReactionModalPost(null)}
        post={reactionModalPost}
      />
    </div>
  );
};

export default CommunityHub;
